import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    FormControlLabel,
    Switch,
    Tabs,
    Tab,
    List,
    ListItem,
    IconButton,
    ListItemText,
    Typography,
    Divider,
    Grid,
    MenuItem
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import StandardFormDialog from '../../components/StandardFormDialog';
import api from '../../services/api';
import { formatarCNPJ } from '../../utils/formatadores';

export default function CadastroParceiro({ open, onClose, parceiroId, onSuccess }) {
    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(false);

    // Dados do Parceiro
    const [formData, setFormData] = useState({
        st_nome: '',
        st_fantasia: '',
        st_cnpj: '',
        en_categoria: 'CLINICA',
        bo_ativo: true,
        unidades: [] // Array de { st_nome_unidade, st_endereco_completo, st_cidade, st_estado, st_telefone }
    });

    // Unidade sendo editada/criada
    const [unidadeEdit, setUnidadeEdit] = useState({
        st_nome_unidade: '',
        st_endereco_completo: '',
        st_cidade: '',
        st_estado: '',
        st_telefone: ''
    });
    const [unidadeIndex, setUnidadeIndex] = useState(-1); // -1 = creating, >=0 = editing index
    const [dialogUnidadeOpen, setDialogUnidadeOpen] = useState(false);

    const categorias = ['CLINICA', 'LABORATORIO', 'CONSULTORIO', 'HOSPITAL', 'FARMACIA', 'OTICA', 'OUTRO'];

    useEffect(() => {
        if (parceiroId) {
            carregarParceiro(parceiroId);
        }
    }, [parceiroId]);

    const carregarParceiro = async (id) => {
        setLoading(true);
        try {
            const res = await api.get(`/parceiro/${id}`);
            const dados = res.data.dados;
            setFormData(dados);
        } catch (err) {
            console.error("Erro ao carregar parceiro", err);
        } finally {
            setLoading(false);
        }
    };

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // ... (rest of state definitions remain)

    const handleSave = async () => {
        // Validação básica
        if (!formData.st_nome || !formData.st_cnpj) {
            setError("Nome e CNPJ são obrigatórios.");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const payload = {
                ...formData,
                st_cnpj: formData.st_cnpj.replace(/\D/g, '') // Clean CNPJ
            };

            if (parceiroId) {
                await api.put(`/parceiro/${parceiroId}`, payload);
                setSuccess("Parceiro atualizado com sucesso!");
            } else {
                await api.post('/parceiro', payload);
                setSuccess("Parceiro criado com sucesso!");
            }

            setTimeout(() => {
                onSuccess("Operação realizada com sucesso!");
            }, 1500);

        } catch (err) {
            console.error(err);
            setError("Erro ao salvar parceiro.");
        } finally {
            setLoading(false);
        }
    };

    // ... (rest of unit handling remains)

    // --- Gestão de Unidades ---
    const handleOpenUnidade = (index = -1) => {
        setUnidadeIndex(index);
        if (index >= 0) {
            setUnidadeEdit({ ...formData.unidades[index] });
        } else {
            setUnidadeEdit({
                st_nome_unidade: '',
                st_endereco_completo: '',
                st_cidade: '',
                st_estado: '',
                st_telefone: ''
            });
        }
        setDialogUnidadeOpen(true);
    };

    const handleSaveUnidade = () => {
        if (!unidadeEdit.st_nome_unidade) {
            alert("Nome da unidade é obrigatório");
            return;
        }

        setFormData(prev => {
            const newUnidades = [...prev.unidades];
            if (unidadeIndex >= 0) {
                newUnidades[unidadeIndex] = unidadeEdit;
            } else {
                newUnidades.push(unidadeEdit);
            }
            return { ...prev, unidades: newUnidades };
        });
        setDialogUnidadeOpen(false);
    };

    const handleDeleteUnidade = (index) => {
        setFormData(prev => ({
            ...prev,
            unidades: prev.unidades.filter((_, i) => i !== index)
        }));
    };

    const tabDados = (
        <Grid container spacing={2}>
            {formData.st_codigo && (
                <Grid item xs={12}>
                    <TextField
                        label="Código Credenciado"
                        fullWidth
                        value={formData.st_codigo}
                        disabled
                        InputProps={{
                            readOnly: true,
                            style: { fontWeight: 'bold', color: '#20ac9c' }
                        }}
                    />
                </Grid>
            )}
            <Grid item xs={12}>
                <TextField
                    label="Razão Social"
                    fullWidth
                    value={formData.st_nome}
                    onChange={e => setFormData({ ...formData, st_nome: e.target.value })}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    label="CNPJ"
                    fullWidth
                    value={formData.st_cnpj}
                    onChange={e => setFormData({ ...formData, st_cnpj: e.target.value })}
                    placeholder="00.000.000/0000-00"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    label="Nome Fantasia"
                    fullWidth
                    value={formData.st_fantasia}
                    onChange={e => setFormData({ ...formData, st_fantasia: e.target.value })}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    select
                    label="Categoria"
                    fullWidth
                    value={formData.en_categoria}
                    onChange={e => setFormData({ ...formData, en_categoria: e.target.value })}
                >
                    {categorias.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
                </TextField>
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.bo_ativo}
                            onChange={e => setFormData({ ...formData, bo_ativo: e.target.checked })}
                        />
                    }
                    label="Parceiro Ativo"
                />
            </Grid>
        </Grid>
    );

    const tabUnidades = (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                    Liste os endereços onde este parceiro atende.
                </Typography>
                <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => handleOpenUnidade(-1)}>
                    Adicionar Unidade
                </Button>
            </Box>

            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {formData.unidades.map((unidade, idx) => (
                    <React.Fragment key={idx}>
                        <ListItem
                            secondaryAction={
                                <Box>
                                    <IconButton edge="end" onClick={() => handleOpenUnidade(idx)}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton edge="end" onClick={() => handleDeleteUnidade(idx)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            }
                        >
                            <ListItemText
                                primary={unidade.st_nome_unidade || "Unidade sem nome"}
                                secondary={`${unidade.st_endereco_completo || ''} - ${unidade.st_cidade}/${unidade.st_estado}`}
                            />
                        </ListItem>
                        <Divider />
                    </React.Fragment>
                ))}
                {formData.unidades.length === 0 && (
                    <Typography sx={{ p: 2, textAlign: 'center' }}>Nenhuma unidade cadastrada.</Typography>
                )}
            </List>
        </Box>
    );

    return (
        <>
            <StandardFormDialog
                open={open}
                onClose={onClose}
                title={parceiroId ? "Editar Parceiro" : "Novo Parceiro"}
                tabs={[
                    { label: "Dados Cadastrais", content: tabDados },
                    { label: "Unidades de Atendimento", content: tabUnidades }
                ]}
                onSave={handleSave}
                loading={loading}
                error={error}
                success={success}
                onErrorClose={() => setError(null)}
                onSuccessClose={() => setSuccess(null)}
                maxWidth="md"
            />

            {/* Sub-Dialog para Unidade */}
            <Dialog open={dialogUnidadeOpen} onClose={() => setDialogUnidadeOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{unidadeIndex >= 0 ? "Editar Unidade" : "Nova Unidade"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Nome da Unidade (ex: Matriz, Filial Centro)"
                            fullWidth
                            value={unidadeEdit.st_nome_unidade}
                            onChange={e => setUnidadeEdit({ ...unidadeEdit, st_nome_unidade: e.target.value })}
                        />
                        <TextField
                            label="Endereço Completo"
                            fullWidth
                            multiline
                            rows={2}
                            value={unidadeEdit.st_endereco_completo}
                            onChange={e => setUnidadeEdit({ ...unidadeEdit, st_endereco_completo: e.target.value })}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Cidade"
                                fullWidth
                                value={unidadeEdit.st_cidade}
                                onChange={e => setUnidadeEdit({ ...unidadeEdit, st_cidade: e.target.value })}
                            />
                            <TextField
                                label="UF"
                                sx={{ width: 80 }}
                                value={unidadeEdit.st_estado}
                                onChange={e => setUnidadeEdit({ ...unidadeEdit, st_estado: e.target.value })}
                            />
                        </Box>
                        <TextField
                            label="Telefone da Unidade"
                            fullWidth
                            value={unidadeEdit.st_telefone}
                            onChange={e => setUnidadeEdit({ ...unidadeEdit, st_telefone: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogUnidadeOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveUnidade} variant="contained">Confirmar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
